import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { Checkbox } from 'antd';
import { ILoginRequest } from 'Data/interfaces/Auth/ILoginRequest';
import {
  getDefaultInfoTheme,
  getDomain,
  getDomainLoading,
} from 'Store/MultiDomain/MultiDomain.selector';
import { AuthActionKeys } from 'Store/Auth/Auth.actions';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { TenantActions } from 'Store/Tenant/Tenant.actions';
import AppStorage from 'Utils/AppStorage';
import { Http } from 'Utils/Http';
import toastHandler from 'Utils/toastHandler';
import { loadImage } from 'Utils/MultidomailUtils';
import BoxContainer from 'Components/UI/Containers/BoxContainer/BoxContainer';
import Field from 'Components/UI/Field';
import Button from 'Components/UI/Button';
import Form from 'Components/UI/Form/Form';
import GoogleLinkButton from 'Components/UI/GoogleLinkButton';
import Link from 'Components/UI/Link';
import Background from '../Backgroud/background';
import schema from './login.schema';
import styles from './Login.module.scss';

import poweredBy from 'Static/images/powered-by.png';

const Login: FC<Props> = (props) => {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();

  const domain = useSelector(getDomain);
  const defaultInfoTheme = useSelector(getDefaultInfoTheme);
  const domainLoading = useSelector(getDomainLoading);
  const location = useLocation();
  const params = useParams();
  const redirectUrl = window.location.origin + location.pathname + location.search;

  const [msgErro, setMsgErro] = useState(Http.GetQueryParams('erro'));
  const [showPassword, setShowPassword] = useState(false);

  if (msgErro) {
    toastHandler.showError(msgErro);
    setMsgErro(null);
    window.history.pushState('data', 'Coordly', '/login');
  }

  useEffect(() => {
    if (params.emailHash) {
      dispatch(TenantActions.getTenantInviteInfo(params.emailHash));
    }
  }, [dispatch, params.emailHash]);

  const loginSubmit = (values: ILoginRequest) => {
    if (params.emailHash) {
      values.TenantInviteEmailHash = params.emailHash;
    }

    props.login({ beforeUrl: redirectUrl, ...values });
  };

  useEffect(() => {
    if (params?.emailHash) {
      dispatch(TenantActions.getTenantInviteInfo(params.emailHash));
    }
  }, [dispatch, params?.emailHash]);

  return (
    <Background loading={domainLoading}>
      <div className={styles['containerWrapper']}>
        <BoxContainer height={630} width={805}>
          <div className={styles['contentWrapper']}>
            <div
              className={styles['halfWrapper']}
              style={{
                background: domainLoading
                  ? ''
                  : `url(${loadImage(domain?.LoginBannerUrl || defaultInfoTheme.LoginBannerUrl)}) no-repeat center/contain`,
              }}
            />
            <div className={`${styles['halfWrapper']} ${styles['formLoginWrapper']}`}>
              <div className={styles['logo-container']}>
                {!domainLoading && (
                  <img
                    alt='logo'
                    src={loadImage(domain?.LogoUrl || defaultInfoTheme.LogoUrl)}
                    style={{ maxWidth: 100, maxHeight: 60 }}
                  />
                )}
                {domain?.LogoUrl && (
                  <img src={poweredBy} alt='decorative powered by logo' />
                )}
              </div>
              <h2 className={styles['titleLogin']}>
                <div style={{ fontFamily: domain.theme.font?.name }}>{t('login.welcome')}</div>
                <div
                  style={{
                    fontFamily: domain.theme.font?.name,
                    color: domain.theme.colors.primary.primary,
                  }}
                >
                  {domainLoading
                    ? ''
                    : (domain.Tenant?.CompanyName || defaultInfoTheme.Tenant?.CompanyName)
                  }
                </div>
              </h2>
              <h3
                style={{ fontFamily: domain.theme.font?.name }}
                className={styles['subtitleLogin']}
              >
                {t('login.subtitle')}
              </h3>
              <Form schema={schema} onSubmit={loginSubmit}>
                <Field
                  name='username'
                  label={t('common:email')}
                  value={props.tenantInviteInfo?.InvitedEmail}
                  icon='menuGerenciarUsuario'
                  placeholder={t('signup.emailPlaceholder')}
                  disabled={!!params.emailHash}
                />
                <Field
                  name='password'
                  label={t('common:password')}
                  icon='cadeado'
                  type={showPassword ? 'text' : 'password'}
                  rightIcon={showPassword ? 'notVisualizar' : 'visualizar'}
                  rightIconSize={12}
                  onRightIconClick={() => setShowPassword((prev) => !prev)}
                />
                <div className={styles['esqueceuSenha']}>
                  <Checkbox onChange={(e) => AppStorage.SetRememberMe(e.target.checked)}>
                    <span
                      style={{
                        fontFamily: domain.theme.font?.name,
                      }}
                    >
                      {t('login.rememberData')}
                    </span>
                  </Checkbox>
                  <Link to='/forgotpassword'>
                    {t('login.forgotPassword')}
                  </Link>
                </div>
                <Button
                  width='100%'
                  type='primary'
                  htmlType='submit'
                  loading={props.isLoading}
                  domainloading={`${domainLoading}`}
                >
                  {t('login.loginButton')}
                </Button>
              </Form>
              <GoogleLinkButton
                emailHash={params.emailHash}
                redirectUrl={redirectUrl}
              />
              <div
                className={styles['novoCadastro']}
                style={{ fontFamily: domain.theme.font?.name }}
              >
                {t('login.noAccount')}{' '}
                <Link
                  to={`/signup${params.emailHash ? `/${params.emailHash}` : ''}`}
                >
                  {t('login.createNow')}
                </Link>
              </div>
            </div>
          </div>
        </BoxContainer>
      </div>
    </Background>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.auth,
  tenantInviteInfo: state.tenant.TenantInviteInfo,
});

const mapDispatch = {
  login: (data: ILoginRequest) => ({
    type: AuthActionKeys.AUTH_LOGIN_REQUEST,
    payload: data,
  }),
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(Login);
